import { AnyRoute, createRoute, Outlet, redirect } from '@tanstack/react-router';
import SearchPage from '@screens/search/SearchPage';
import { AffiliateProgramPage } from '@screens/affiliateProgram/AffiliateProgramPage';
import StudentsPage from '@screens/students/Students';
import SingleStudentPage from '@screens/students/SingleStudent';
import { createDegreeRoutes } from './degreeRoutes';
import { createCourseRoutes } from './courseRoutes';
import { createLessonRoutes } from './lessonRoutes';
import { createRoutesFromConfig, RouteConfig } from './routeHelpers';
import { ConnectedAppLayout } from '../layout';
import { TutorialsPage } from '@new/features/newlane-tutorials/pages/TutorialsPage';
import { createAdminRouteTree } from './adminRoutes';
import { OnboardingPage } from '@new/features/onboarding/pages/OnboardingPage';
import { SubscribePage } from '@new/features/subscription/pages/SubscribePage';
import { RequireUnsubscribed } from '@new/features/subscription/components/RequireUnsubscribed';
import { ProfilePage } from '@new/features/profile/pages/ProfilePage';
import { ChatPage } from '@new/features/chat/pages/ChatPage';
import { InstructorSchedulePage } from '@new/features/schedule/pages/InstructorSchedulePage';
import { ProjectsReview } from '@new/features/projects-review/components/ProjectsReview';
import { CourseHearingsPage } from '@new/features/course-hearings/pages/CourseHearingsPage';
import { ProtectedRoute } from '@new/casl/components/ProtectedRoute';
import { ObjectiveDetailPage } from '@new/features/objectives/pages/ObjectiveDetailPage';
import Home from '@screens/home/Home';
import { StudentTranscriptsPage } from '@new/features/transcripts/student/pages/StudentTranscriptsPage';
import { TranscriptsReviewPage } from '@new/features/transcripts/admin/pages/TranscriptsReviewPage';
import { CourseBuilderPage } from '@new/features/course-builder/pages/CourseBuilderPage';

const ScheduleRoute = () => (
  <ProtectedRoute action="access" subject="Schedule">
    <InstructorSchedulePage />
  </ProtectedRoute>
);

const ProjectsRoute = () => (
  <ProtectedRoute action="access" subject="ProjectsReview">
    <ProjectsReview />
  </ProtectedRoute>
);

const TranscriptsReviewRoute = () => (
  <ProtectedRoute action="access" subject="TranscriptsReview">
    <TranscriptsReviewPage />
  </ProtectedRoute>
);

const CourseHearingsRoute = () => (
  <ProtectedRoute action="access" subject="CourseHearing">
    <CourseHearingsPage />
  </ProtectedRoute>
);

const StudentsRoute = () => (
  <ProtectedRoute action="read" subject="Student">
    <StudentsPage />
  </ProtectedRoute>
);

const SingleStudentRoute = () => (
  <ProtectedRoute action="read" subject="Student">
    <SingleStudentPage />
  </ProtectedRoute>
);

const OnboardingRoute = () => {
  return (
    <ProtectedRoute action="access" subject="Onboarding">
      <OnboardingPage />
    </ProtectedRoute>
  );
};

const ChatRoute = () => (
  <ProtectedRoute action="access" subject="Chat">
    <ChatPage />
  </ProtectedRoute>
);

const SearchRoute = () => (
  <ProtectedRoute action="access" subject="SearchBar">
    <SearchPage />
  </ProtectedRoute>
);

const SubscribeRoute = () => (
  <RequireUnsubscribed>
    <SubscribePage />
  </RequireUnsubscribed>
);

const TranscriptsRoute = () => <StudentTranscriptsPage />;

const CourseBuilderRoute = () => <CourseBuilderPage />;

const privateRouteConfigs: RouteConfig[] = [
  { path: '/', component: Home },
  { path: 'search', component: SearchRoute },
  { path: 'edit-profile', component: ProfilePage },
  { path: 'transcripts', component: TranscriptsRoute },
  { path: 'newlane-tutorials', component: TutorialsPage },
  { path: 'chat', component: ChatRoute },
  // { path: 'user-agreements', component: SignAgreement },
  { path: 'affiliate-program', component: AffiliateProgramPage },
  { path: 'schedule', component: ScheduleRoute },
  { path: 'projects', component: ProjectsRoute },
  { path: 'transcripts-review', component: TranscriptsReviewRoute },
  { path: 'course-builder', component: CourseBuilderRoute },
  { path: 'course-hearings', component: CourseHearingsRoute },
  { path: 'objectives/$objectiveId', component: ObjectiveDetailPage },
  {
    path: 'students',
    component: StudentsRoute,
  },
  {
    path: 'students/$id',
    component: SingleStudentRoute,
  },
  {
    path: 'onboarding',
    component: OnboardingRoute,
  },
  {
    path: 'subscribe',
    component: SubscribeRoute,
  },
  {
    path: 'library',
    component: () => {
      window.location.href = 'https://app.newlane.edu/jstor.html';
      return null;
    },
  },
];

export const createPrivateRoutes = (parent: AnyRoute) => {
  const privateRoutes = createRoutesFromConfig(parent, privateRouteConfigs);
  return [
    ...privateRoutes,
    ...createDegreeRoutes(parent),
    ...createCourseRoutes(parent),
    ...createLessonRoutes(parent),
  ];
};

export const createPrivateRouteTree = (rootRoute: AnyRoute) => {
  const privateRoute = createRoute({
    getParentRoute: () => rootRoute,
    id: 'private',
    beforeLoad: async () => {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        return redirect({ to: '/login' });
      }
    },
    component: () => (
      <ConnectedAppLayout>
        <Outlet />
      </ConnectedAppLayout>
    ),
  });

  const privateRoutes = createPrivateRoutes(privateRoute);
  const adminRoutes = createAdminRouteTree(privateRoute);

  privateRoute.addChildren([...privateRoutes, adminRoutes]);

  return privateRoute;
};
